import React from 'react';
import { CaretRight, CaretDown, Star, Folder, FolderOpen, File, BracketsAngle, X, Stack, Prohibit, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Text, TagBadge } from '@chatsdet/ui';
import { formatDistanceToNow } from 'date-fns';
import { collectPageIds, isSubtreeFullyExcluded } from '../types';
import type { PathTreeNode } from '../types';

type SiteGraphTreeNodeProps = {
  treeNode: PathTreeNode;
  depth: number;
  expandedPaths: Set<string>;
  onToggle: (path: string) => void;
  onNavigate: (canonicalPageId: string, propertyId: string) => void;
  onMarkSlug?: (parentPath: string, mode: 'slug' | 'not-slug') => void;
  onRemoveOverride?: (parentPath: string) => void;
  onToggleExclude?: (canonicalPageIds: string[], excluded: boolean) => void;
  propertyId: string;
};

export function SiteGraphTreeNode({
  treeNode,
  depth,
  expandedPaths,
  onToggle,
  onNavigate,
  onMarkSlug,
  onRemoveOverride,
  onToggleExclude,
  propertyId,
}: SiteGraphTreeNodeProps) {
  const { segment, fullPath, node, children, slugPattern, pageCount } = treeNode;
  const isExpanded = expandedPaths.has(fullPath);
  const hasChildren = children.length > 0;
  const isClickable = !!node;
  const isFolder = hasChildren && !node;
  const isSlug = !!slugPattern;
  const canGroupAsSlug = isFolder && !isSlug && children.filter((c) => c.node && c.children.length === 0).length >= 2;

  // QA exclusion: a leaf page reflects its own flag; a folder reflects whether
  // every descendant page is excluded. Slug-pattern nodes are not toggleable here
  // (per-instance flags aren't available in the tree payload).
  const subtreeExcluded = node ? node.qaExcluded : isFolder && isSubtreeFullyExcluded(treeNode);
  const canToggleExclude = !!onToggleExclude && !isSlug && (!!node || (isFolder && pageCount > 0));
  const handleToggleExclude = (e: React.MouseEvent) => {
    e.stopPropagation();
    const ids = collectPageIds(treeNode);
    if (ids.length > 0) onToggleExclude?.(ids, !subtreeExcluded);
  };

  const handleClick = () => {
    if (node) {
      onNavigate(node.id, propertyId);
    } else if (hasChildren) {
      onToggle(fullPath);
    }
  };

  const displaySegment = segment || '/';

  // Icon based on node type
  const nodeIcon = isSlug ? (
    <BracketsAngle size={14} weight="duotone" className="text-muted-foreground shrink-0" />
  ) : isFolder ? (
    isExpanded ? (
      <FolderOpen size={14} weight="duotone" className="text-amber-500 shrink-0" />
    ) : (
      <Folder size={14} weight="duotone" className="text-amber-500 shrink-0" />
    )
  ) : node ? (
    <File size={14} weight="duotone" className="text-muted-foreground/60 shrink-0" />
  ) : null;

  return (
    <>
      <div
        className={[
          'group flex items-center gap-2 py-1.5 pr-3 text-sm transition-colors relative',
          isClickable
            ? 'cursor-pointer hover:bg-accent/80 active:bg-accent'
            : hasChildren
              ? 'cursor-pointer hover:bg-muted/50'
              : '',
          node?.isEntrypoint ? 'bg-primary/[0.03]' : '',
          subtreeExcluded ? 'opacity-50' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ paddingLeft: `${depth * 20 + 12}px` }}
        onClick={handleClick}
      >
        {/* Indentation guides */}
        {depth > 0 &&
          Array.from({ length: depth }).map((_, i) => (
            <span
              key={i}
              className="absolute top-0 bottom-0 border-l border-border/40"
              style={{ left: `${i * 20 + 20}px` }}
            />
          ))}

        {/* Expand/collapse caret */}
        <button
          className="flex items-center justify-center w-4 h-4 shrink-0 rounded hover:bg-accent"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(fullPath);
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <CaretDown size={12} weight="bold" className="text-muted-foreground" />
            ) : (
              <CaretRight size={12} weight="bold" className="text-muted-foreground" />
            )
          ) : (
            <span className="w-4" />
          )}
        </button>

        {/* Node icon */}
        {nodeIcon}

        {/* Segment name */}
        {isSlug ? (
          <span className="font-mono text-xs text-muted-foreground italic">
            /{displaySegment}
          </span>
        ) : (
          <span
            className={[
              'font-mono text-xs truncate',
              node ? 'text-foreground' : 'text-muted-foreground',
            ].join(' ')}
          >
            /{displaySegment}
          </span>
        )}

        {/* Page title */}
        {node?.title && (
          <Text size="xs" className="text-muted-foreground truncate max-w-[240px] ml-0.5">
            {node.title}
          </Text>
        )}

        {/* Badges */}
        <div className="flex items-center gap-1 shrink-0">
          {node?.isEntrypoint && (
            <TagBadge
              name={node.entrypointLabel ?? 'Entrypoint'}
              color="yellow"
              size="sm"
              icon={<Star size={10} weight="fill" />}
            />
          )}

          {isSlug && (
            <>
              <TagBadge name={<>{slugPattern.instanceCount} pages</>} color="gray" hideDot className="text-[10px] px-1.5 py-0 leading-tight font-medium tabular-nums" />
              {slugPattern.isOverride && (
                <TagBadge name="Manual" color="gray" hideDot className="text-[10px] px-1.5 py-0 leading-tight font-medium text-primary" />
              )}
            </>
          )}

          {subtreeExcluded && (
            <TagBadge
              name="QA excluded"
              color="gray"
              size="sm"
              icon={<Prohibit size={10} weight="bold" />}
            />
          )}
        </div>

        {/* Inline hover action: exclude/include from QA */}
        {canToggleExclude && (
          <button
            className={[
              'hidden group-hover:flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] transition-colors',
              subtreeExcluded
                ? 'text-muted-foreground hover:text-primary hover:bg-primary/10'
                : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
            ].join(' ')}
            onClick={handleToggleExclude}
            title={
              subtreeExcluded
                ? 'Include in QA runs'
                : isFolder
                  ? 'Exclude all pages under this path from QA runs'
                  : 'Exclude this page from QA runs'
            }
          >
            {subtreeExcluded ? (
              <>
                <ArrowCounterClockwise size={10} weight="bold" />
                Include
              </>
            ) : (
              <>
                <Prohibit size={10} weight="bold" />
                Exclude
              </>
            )}
          </button>
        )}

        {/* Inline hover actions: group/ungroup */}
        {isSlug && onMarkSlug && (
          <button
            className="hidden group-hover:flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const path = slugPattern.parentPath || '/';
              if (slugPattern.isOverride) {
                onRemoveOverride?.(path);
              } else {
                onMarkSlug(path, 'not-slug');
              }
            }}
          >
            <X size={10} weight="bold" />
            Ungroup
          </button>
        )}
        {canGroupAsSlug && onMarkSlug && (
          <button
            className="hidden group-hover:flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const parentPath = fullPath.startsWith('/') ? fullPath : `/${fullPath}`;
              onMarkSlug(parentPath, 'slug');
            }}
          >
            <Stack size={10} weight="bold" />
            Group
          </button>
        )}

        {/* Right-aligned metadata */}
        <div className="ml-auto flex items-center gap-3 shrink-0 tabular-nums">
          {node && (
            <Text size="xs" className="text-muted-foreground/70 text-right whitespace-nowrap">
              {formatDistanceToNow(new Date(node.lastSeenAt), { addSuffix: true })}
            </Text>
          )}
          {pageCount > 0 && !node && !isSlug && (
            <Text size="xs" className="text-muted-foreground/50 w-8 text-right">
              {pageCount}
            </Text>
          )}
        </div>
      </div>

      {isExpanded &&
        children.map((child) => (
          <SiteGraphTreeNode
            key={child.fullPath}
            treeNode={child}
            depth={depth + 1}
            expandedPaths={expandedPaths}
            onToggle={onToggle}
            onNavigate={onNavigate}
            onMarkSlug={onMarkSlug}
            onRemoveOverride={onRemoveOverride}
            onToggleExclude={onToggleExclude}
            propertyId={propertyId}
          />
        ))}
    </>
  );
}
